import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { CalendarPlus, Check, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ALERT_LEAD_MINUTES,
  ALERT_TRAIL_MINUTES,
  EVENT_TEAMS,
  TEAM_LABELS,
  addAlertEvent,
  removeAlertEvent,
  updateAlertEvent,
  upsertAlertEvent,
  type AlertEvent,
  type EventTeam,
} from '@/lib/api/alertEvents';
import { SEASON_2026 } from './season2026';

const toInputValue = (d: Date) => format(d, "yyyy-MM-dd'T'HH:mm");

const emptyForm = () => ({
  team: 'DOLPHINS' as EventTeam,
  opponent: '',
  kickoff: '',
  timeTbd: false,
  note: '',
});

type Props = {
  events: AlertEvent[] | undefined;
  now: number;
};

export function ScheduleTab({ events, now }: Props) {
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showPast, setShowPast] = useState(false);
  const [importing, setImporting] = useState(false);

  const visible = useMemo(() => {
    const list = events ?? [];
    return showPast
      ? list
      : list.filter((e) => e.windowEnd.getTime() > now - 60 * 60_000);
  }, [events, showPast, now]);

  const startEdit = (event: AlertEvent) => {
    setEditingId(event.id ?? null);
    setForm({
      team: event.team,
      opponent: event.opponent,
      kickoff: toInputValue(event.kickoff),
      timeTbd: event.timeTbd,
      note: event.note ?? '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const submit = async () => {
    const opponent = form.opponent.trim();
    if (!opponent || !form.kickoff) {
      toast.error('Opponent and date/time are required.');
      return;
    }
    const kickoff = new Date(form.kickoff);
    if (Number.isNaN(kickoff.getTime())) {
      toast.error('Invalid date/time.');
      return;
    }
    const input = {
      team: form.team,
      opponent,
      kickoff,
      timeTbd: form.timeTbd,
      note: form.note,
    };
    try {
      if (editingId) {
        await updateAlertEvent(editingId, input);
        toast.success('Event updated.');
      } else {
        await addAlertEvent(input);
        toast.success('Event added.');
      }
      cancelEdit();
    } catch (e) {
      toast.error(`Save failed: ${(e as Error).message}`);
    }
  };

  const onRemove = async (event: AlertEvent) => {
    if (!event.id) return;
    try {
      await removeAlertEvent(event.id);
      if (editingId === event.id) cancelEdit();
      toast.success('Event removed.');
    } catch (e) {
      toast.error(`Delete failed: ${(e as Error).message}`);
    }
  };

  // Adds only the games that aren't in Firestore yet, so re-running never
  // clobbers a kickoff time an admin has corrected by hand.
  const importSeason = async () => {
    setImporting(true);
    const existing = new Set((events ?? []).map((e) => e.id));
    const missing = SEASON_2026.filter((g) => !existing.has(g.id));
    try {
      await Promise.all(
        missing.map((g) =>
          upsertAlertEvent(g.id, {
            team: g.team,
            opponent: g.opponent,
            kickoff: new Date(g.kickoff),
            timeTbd: g.timeTbd,
            note: g.note,
          })
        )
      );
      toast.success(
        missing.length
          ? `Imported ${missing.length} game${missing.length === 1 ? '' : 's'}.`
          : 'Schedule already up to date.'
      );
    } catch (e) {
      toast.error(`Import failed: ${(e as Error).message}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Event schedule</h2>
          <p className="text-sm text-muted-foreground">
            Alerting is armed from kickoff &minus; {ALERT_LEAD_MINUTES / 60} h
            until kickoff + {ALERT_TRAIL_MINUTES / 60} h. Games whose kickoff is
            still TBA cover the whole game day (07:00 to midnight) until a time
            is set.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Checkbox
              checked={showPast}
              onCheckedChange={(v) => setShowPast(!!v)}
            />
            Show past events
          </label>
          <Button variant="outline" onClick={importSeason} disabled={importing}>
            <CalendarPlus className="mr-1 h-4 w-4" />
            Import 2026 home schedule
          </Button>
        </div>
      </div>

      {/* Add / edit form */}
      <div className="mt-4 rounded-md border p-4">
        <p className="mb-3 font-medium">
          {editingId ? 'Edit event' : 'Add an event'}
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="event-team">Team</Label>
            <Select
              value={form.team}
              onValueChange={(v) =>
                setForm((f) => ({ ...f, team: v as EventTeam }))
              }
            >
              <SelectTrigger id="event-team" className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EVENT_TEAMS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {TEAM_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="event-opponent">Opponent / title</Label>
            <Input
              id="event-opponent"
              placeholder="Buffalo Bills"
              value={form.opponent}
              onChange={(e) =>
                setForm((f) => ({ ...f, opponent: e.target.value }))
              }
              className="w-[220px]"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="event-kickoff">Kickoff (local time)</Label>
            <Input
              id="event-kickoff"
              type="datetime-local"
              value={form.kickoff}
              onChange={(e) =>
                setForm((f) => ({ ...f, kickoff: e.target.value }))
              }
              className="w-[220px]"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="event-note">Note</Label>
            <Input
              id="event-note"
              placeholder="Week 3, home opener"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              className="w-[220px]"
            />
          </div>
          <label className="flex h-9 items-center gap-2 text-sm">
            <Checkbox
              checked={form.timeTbd}
              onCheckedChange={(v) => setForm((f) => ({ ...f, timeTbd: !!v }))}
            />
            Kickoff time TBA
          </label>
          <Button onClick={submit}>
            {editingId ? (
              <>
                <Check className="mr-1 h-4 w-4" />
                Save
              </>
            ) : (
              <>
                <Plus className="mr-1 h-4 w-4" />
                Add event
              </>
            )}
          </Button>
          {editingId && (
            <Button variant="ghost" onClick={cancelEdit}>
              <X className="mr-1 h-4 w-4" />
              Cancel
            </Button>
          )}
        </div>
      </div>

      <div className="mt-4 rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Event</TableHead>
              <TableHead>Kickoff</TableHead>
              <TableHead>Alerting window</TableHead>
              <TableHead>Note</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  No events scheduled — import the 2026 home schedule or add one
                  above.
                </TableCell>
              </TableRow>
            ) : (
              visible.map((e) => {
                const live =
                  e.windowStart.getTime() <= now && e.windowEnd.getTime() > now;
                const past = e.windowEnd.getTime() <= now;
                return (
                  <TableRow key={e.id} className={past ? 'opacity-60' : ''}>
                    <TableCell className="font-medium">
                      {TEAM_LABELS[e.team]}
                      {e.opponent ? ` vs ${e.opponent}` : ''}
                      {live && (
                        <Badge variant="destructive" className="ml-2">
                          Window open
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {format(e.kickoff, 'EEE MMM d, yyyy')}
                      {e.timeTbd ? (
                        <Badge variant="outline" className="ml-2">
                          TBA
                        </Badge>
                      ) : (
                        ` · ${format(e.kickoff, 'HH:mm')}`
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {format(e.windowStart, 'MMM d HH:mm')} to{' '}
                      {format(e.windowEnd, 'MMM d HH:mm')}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {e.note}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEdit(e)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onRemove(e)}
                        >
                          Remove
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
